import React, { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle, AlertCircle, CreditCard } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "./Button";
import { Card } from "./card";
import CurrencyDropdown from "./CurrencyDropdown";
import PaymentForm from "./PaymentForm";
import EnhancedPaymentForm from "./EnhancedPaymentForm";
import { escrowAPI, Escrow } from "../../services/escrowAPI";
import { GlobalService } from "../../modules";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllCurrencies, createChat, createConversation, getUserChats } from "../../redux/Actions/AuthAction";
import { updateUserJob } from "../../redux/Actions/ClientActions";
import { updateOffer } from "../../redux/Slices/offerSlice";
import { fetchCommissions } from "../../redux/Slices/commissionSlice";
import { getQuotePricingsByCategory } from "../../redux/Actions/AdminQuotePricingActions";
import { calculateQuotePrice } from "../../utils/quotePricingUtils";
import { PLATFORM_CONFIG } from "../../config/platformConfig";
import { JobStatusEnum, ConversationTypeEnum, ContextEnum, CommissionStatusEnum, CommissionCategoryTypeEnum, MilestonePaymentStatusEnum, MilestonePaymentLogCreateDto, MilestoneStatusEnum } from "../../modules/shared/model";
import { OfferStatusEnum } from "./RequestQuoteModal";
import { fetchParticipantChats } from "../../redux";
import { createMilestonePaymentLog } from "../../redux/Slices/contractSlice";
import { updateMilestone } from "../../redux/Slices/milestoneSlice";
import ContractNotificationService from "../../services/contractNotificationService";
import { useCurrencyDetection, getCachedCurrencyPreference, setCachedCurrencyPreference, getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { webSocketService } from "../../services/websocketService";
import { useTranslation } from "react-i18next";
import { getPlatformFeeForContract } from "../../utils/categoryFormSettingsUtils";
import { fetchLatestCategoryFormSettings } from "../../redux/Actions/CategoryFormSettingsAction";

interface Props {
	isOpen: boolean;
	onClose: () => void;
	onPaymentSuccess?: () => void;
	milestone: any;
	selectedContract?: any
	clientId: string;
	professionalId: string;
}

type PaymentStep = "form" | "processing" | "success" | "error";

const PaymentModal: React.FC<Props> = ({ isOpen, onClose, onPaymentSuccess, milestone, clientId, professionalId, selectedContract }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();
	const { currencies } = useAppSelector((state: any) => state.auth);
	const { items: commissionRules = [] } = useAppSelector((state: any) => state.commissions);
	const { items: quotePricings = [] } = useAppSelector((state: any) => state.adminQuotePricings);
	const { setting: latestCategoryFormSetting, settings: allCategoryFormSettings } = useAppSelector((state: any) => state.categoryFormSettings);
	const categoryFormSetting = latestCategoryFormSetting || (Array.isArray(allCategoryFormSettings) && allCategoryFormSettings.length > 0 ? allCategoryFormSettings[0] : allCategoryFormSettings);
	const [paymentStep, setPaymentStep] = useState<PaymentStep>("form");
	const [escrow, setEscrow] = useState<Escrow | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [redirectCountdown, setRedirectCountdown] = useState(3);
	const [selectedCurrencyId, setSelectedCurrencyId] = useState<string>("1"); // Default to USD
	const [selectedCurrency, setSelectedCurrency] = useState<{id: string; name: string; code: string} | null>(null);
	const [isDetectingCurrency, setIsDetectingCurrency] = useState(false);
	const [isInitializing, setIsInitializing] = useState(false);
	const [isClosingAfterPayment, setIsClosingAfterPayment] = useState(false);
	const [useEnhancedForm, setUseEnhancedForm] = useState(true); // Toggle between old and new form
	const [availableBalance, setAvailableBalance] = useState<number>(0);
	const [checkingBalance, setCheckingBalance] = useState(false);
	const [useExistingFunds, setUseExistingFunds] = useState(false);
	const [localQuotePricings, setLocalQuotePricings] = useState<any[]>([]);
	const { user } = useAppSelector((state: any) => state.auth);

	// Function to check available balance
	const checkAvailableBalance = useCallback(async () => {
		if (!user?.id) return;

		setCheckingBalance(true);
		try {
			const balanceData = await escrowAPI.getClientEscrowBalance(user.id, {
				page: 1,
				limit: 1
			});
			setAvailableBalance(balanceData.availableBalance || 0);
		} catch (error) {
			console.error("Error checking available balance:", error);
			setAvailableBalance(0);
		} finally {
			setCheckingBalance(false);
		}
	}, [user?.id]);



	// Function to calculate withdrawal amount based on commission rules
	const calculatewithdrawlAmount = async (milestoneAmount: number, emergencyCost: number): Promise<number> => {
		console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
		console.log("🔢 COMMISSION CALCULATION START");
		console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
		
		try {
			// Get job data from contract hierarchy
			const job = milestone?.contract?.offer?.job;
			const categoryId = job?.categoryId?.id;
			const isEmergency = job?.isEmergency;
			const province = job?.province;
			const municipality = job?.municipality;
			const city = job?.city;
			const professionalId = milestone?.contract?.professional?.id;
			
			console.log("📊 Input Data:");
			console.log("  Milestone Amount: $" + milestoneAmount);
			console.log("  Emergency Cost: $" + emergencyCost);
			console.log("  Total Amount: $" + (milestoneAmount + emergencyCost));
			console.log("\n🏢 Job Details:");
			console.log("  Category ID:", categoryId);
			console.log("  Is Emergency:", isEmergency);
			console.log("  Province:", province);
			console.log("  Municipality:", municipality);
			console.log("  City:", city);
			
			if (!categoryId) {
				console.warn("⚠️ No category ID - returning original amount");
				console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");
				return milestoneAmount; // Return original amount if no category found
			}

			const totalAmount = milestoneAmount + emergencyCost;

			// ═══════════════════════════════════════════════════════════
			// UPPER LAYER: COMMISSION TIER CHECKING
			// Check if professional's current year released amount crosses tier threshold
			// ═══════════════════════════════════════════════════════════
			console.log("\n🎯 COMMISSION TIER CHECK (UPPER LAYER):");
			console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
			
			// Get category type from job
			const jobCategoryTypeId = job?.categoryId?.type?.id;
			
			if (jobCategoryTypeId && professionalId) {
				console.log("  🔍 Looking for Commission Rule:");
				console.log("    Job Category Type ID:", jobCategoryTypeId);
				console.log("    Is Emergency:", isEmergency);
				console.log("    Professional ID:", professionalId);
				
				// Find active commission rule for this category type
				const activeCommission = commissionRules.find((rule: any) => {
					return Number(rule?.status?.id) === CommissionStatusEnum.Active &&
						Number(rule?.categoryType?.id) === (isEmergency ? CommissionCategoryTypeEnum.Emergency_Service : Number(jobCategoryTypeId));
				});

				console.log("\n  📋 Commission Rule Status:");
				console.log("    Active Rule Found:", activeCommission);
				if (activeCommission) {
					console.log("    Rule ID:", activeCommission.id);
					console.log("    Category Type:", activeCommission.categoryType?.name || "Unknown");
					console.log("    Base Rate:", (activeCommission.baseRate || 0) + "%");
					console.log("    Tiers Count:", activeCommission.commissionTiers?.length || 0);
				}
				
				if (activeCommission && activeCommission.commissionTiers && activeCommission.commissionTiers.length > 0) {
					console.log("\n  💎 Commission Tiers Configuration:");
					activeCommission.commissionTiers.forEach((tier: any, index: number) => {
						console.log(`    Tier ${index + 1}: ${tier.name?.name || "Unknown"}`);
						console.log(`      - Threshold: $${tier.threshold || 0}`);
						console.log(`      - Rate: ${tier.rate || 0}%`);
					});
					
					try {
						console.log("\n  📊 Contract Amount Check:");
						const contractTotalAmount = milestone?.contract?.amount || 0;
						const contractId = milestone?.contract?.id;
						
						console.log("    Contract ID:", contractId);
						console.log("    Contract Total Amount: $" + contractTotalAmount.toFixed(2));
						
						// Sort tiers by threshold (highest to lowest)
						const sortedTiers = [...activeCommission.commissionTiers].sort(
							(a: any, b: any) => (b.threshold || 0) - (a.threshold || 0)
						);
						
						console.log("\n  🎲 Evaluating Tiers (Highest to Lowest):");
						// Find first tier where contract total amount >= threshold
						let appliedTier = null;
						for (const tier of sortedTiers) {
							const threshold = tier.threshold || 0;
							const crosses = contractTotalAmount >= threshold;
							console.log(`    ${crosses ? "✅" : "❌"} ${tier.name?.name || "Unknown"}: $${contractTotalAmount.toFixed(2)} ${crosses ? ">=" : "<"} $${threshold} (Discount: ${tier.rate}%)`);
							if (!appliedTier && crosses) {
								appliedTier = tier;
							}
						}
						
						// If tier is found, apply discount to base commission rate
						if (appliedTier) {
							const baseRate = activeCommission.baseRate || 0;
							const tierDiscount = appliedTier.rate || 0;
							const effectiveRate = baseRate - tierDiscount; // Base rate MINUS discount
							const commissionAmount = totalAmount * (effectiveRate / 100);
							const withdrawlAmount = totalAmount - commissionAmount;
							
							const isDefaultTier = (appliedTier.threshold || 0) === 0;
							const threshold = appliedTier.threshold || 0;
							
							console.log("\n  🎉 TIER DISCOUNT APPLIED:");
							console.log("    Applied Tier: " + (appliedTier.name?.name || "Unknown") + (isDefaultTier ? " (Default)" : ""));
							console.log("    Contract Total: $" + contractTotalAmount.toFixed(2));
							console.log("    Tier Threshold: $" + threshold.toFixed(2));
							console.log("    ─────────────────────────────");
							console.log("    Base Commission Rate: " + baseRate + "%");
							console.log("    Tier Discount: -" + tierDiscount + "%");
							console.log("    Effective Rate: " + effectiveRate + "%");
							console.log("    ─────────────────────────────");
							console.log("    Milestone Amount: $" + totalAmount.toFixed(2));
							console.log("    Commission (-): $" + commissionAmount.toFixed(2));
							console.log("    Withdrawal Amount: $" + withdrawlAmount.toFixed(2));
							console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");
							
							// Store tier discount info for notification (sent after payment success)
							if (threshold > 0 && tierDiscount > 0) {
								// Will send notification after first milestone is funded
								(window as any).__tierDiscountInfo = {
									professionalId,
									contractAmount: contractTotalAmount,
									tierThreshold: threshold,
									discountPercentage: tierDiscount,
									tierName: appliedTier.name?.name || "Unknown"
								};
							}
							
							return withdrawlAmount;
						} else {
							console.log("\n  ⚠️ No tier found - this should not happen");
						}
					} catch (tierError) {
						console.error("\n  ❌ Error evaluating commission tiers:", tierError);
						console.log("    Falling back to base commission rate");
						// Continue to fallback logic
					}
				} else {
					console.log("\n  ℹ️ No commission tiers configured - using base rate");
				}
			} else {
				console.log("  ⚠️ Missing required data:");
				if (!jobCategoryTypeId) console.log("    - Job Category Type ID");
				if (!professionalId) console.log("    - Professional ID");
			}
			// ═══════════════════════════════════════════════════════════
			// END OF UPPER LAYER
			// ═══════════════════════════════════════════════════════════

			// Try to use QuotePricing for emergency jobs
			if (isEmergency) {
				const availableQuotePricings = localQuotePricings.length > 0 ? localQuotePricings : quotePricings;
				
				if (availableQuotePricings && availableQuotePricings.length > 0) {
					// Find matching quote pricing based on category and location
					const matchedPricing = availableQuotePricings.find((pricing: any) => {
						// Check if category matches
						const pricingCategoryId = typeof pricing.category === "string" ? pricing.category : pricing.category?.id;
						
						if (pricingCategoryId !== categoryId) return false;

						// Check if any region matches the job location
						if (pricing.regions && pricing.regions.length > 0) {
							const hasMatch = pricing.regions.some((region: any) => {
								const regionName = region.region?.name?.toLowerCase();
								const regionState = region.region?.state?.toLowerCase();
								const regionCity = region.region?.city?.toLowerCase();

								const jobProvince = province?.toLowerCase();
								const jobMunicipality = municipality?.toLowerCase();
								const jobCity = city?.toLowerCase();

								// Check all possible matches
								const matchByName = regionName && (
									(jobProvince && regionName === jobProvince) ||
									(jobMunicipality && regionName === jobMunicipality) ||
									(jobCity && regionName === jobCity)
								);
								
								const matchByState = regionState && (
									(jobProvince && regionState === jobProvince) ||
									(jobMunicipality && regionState === jobMunicipality) ||
									(jobCity && regionState === jobCity)
								);
								
								const matchByCity = regionCity && (
									(jobProvince && regionCity === jobProvince) ||
									(jobMunicipality && regionCity === jobMunicipality) ||
									(jobCity && regionCity === jobCity)
								);

								const match = matchByName || matchByState || matchByCity;
								
								return match;
							});
							
							return hasMatch;
						}

						return false;
					});

					if (matchedPricing && matchedPricing.regions && matchedPricing.regions.length > 0) {
						// Get the first matching region
						const matchedRegion = matchedPricing.regions.find((region: any) => {
							const regionName = region.region?.name?.toLowerCase();
							const regionState = region.region?.state?.toLowerCase();
							const regionCity = region.region?.city?.toLowerCase();

							const jobProvince = province?.toLowerCase();
							const jobMunicipality = municipality?.toLowerCase();
							const jobCity = city?.toLowerCase();

							// Check all possible matches
							const matchByName = regionName && (
								(jobProvince && regionName === jobProvince) ||
								(jobMunicipality && regionName === jobMunicipality) ||
								(jobCity && regionName === jobCity)
							);
							
							const matchByState = regionState && (
								(jobProvince && regionState === jobProvince) ||
								(jobMunicipality && regionState === jobMunicipality) ||
								(jobCity && regionState === jobCity)
							);
							
							const matchByCity = regionCity && (
								(jobProvince && regionCity === jobProvince) ||
								(jobMunicipality && regionCity === jobMunicipality) ||
								(jobCity && regionCity === jobCity)
							);

							return matchByName || matchByState || matchByCity;
						});

						if (matchedRegion && matchedRegion.regularCommission) {
							// Use regularCommission percentage from quote pricing
							const commissionRate = matchedRegion.regularCommission || 0;
							const commissionAmount = totalAmount * (commissionRate / 100);
							const withdrawlAmount = totalAmount - commissionAmount;
							
							return withdrawlAmount;
						}
					}
				}
			}

			// Fallback to existing commission system
			console.log("\n🔄 FALLBACK: Using Base Commission Rate");
			console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
			const categoryTypeId = job?.categoryId?.type?.id;
			
			if (!categoryTypeId) {
				console.warn("⚠️ No category type ID - returning original amount");
				console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");
				return milestoneAmount; // Return original amount if no category type found
			}

			// Find active commission rule for this category type
			const activeCommission = commissionRules.find((rule: any) => {
				return Number(rule?.status?.id) === CommissionStatusEnum.Active && // Active status
					Number(rule?.categoryType?.id) === (isEmergency ? CommissionCategoryTypeEnum.Emergency_Service : Number(categoryTypeId));
			});

			if (!activeCommission) {
				console.warn("  ⚠️ No active commission rule found - returning original amount");
				console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");
				return milestoneAmount; // Return original amount if no commission rule found
			}

			console.log("  Commission Rule ID:", activeCommission.id);
			console.log("  Category Type:", activeCommission.categoryType?.name || "Unknown");
			
			// Calculate withdrawal amount: totalAmount - (totalAmount * baseRate%)
			const baseRate = activeCommission.baseRate || 0;
			const commissionAmount = totalAmount * (baseRate / 100);
			const withdrawlAmount = totalAmount - commissionAmount;
			
			console.log("\n  💵 BASE COMMISSION APPLIED:");
			console.log("    Base Rate: " + baseRate + "%");
			console.log("    ─────────────────────────────");
			console.log("    Total Amount: $" + totalAmount.toFixed(2));
			console.log("    Commission (-): $" + commissionAmount.toFixed(2));
			console.log("    Withdrawal Amount: $" + withdrawlAmount.toFixed(2));
			console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");
			
			return withdrawlAmount;
		} catch (error) {
			console.error("❌ ERROR in commission calculation:", error);
			console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n");
			return milestoneAmount; // Return original amount on error
		}
	};
	// Function to create chat and send payment success message
	const createChatAndSendMessage = async (escrow: Escrow) => {
		try {
			// Check if chat already exists between client and professional
			let existingChat = null;
			try {
				existingChat = await dispatch(fetchParticipantChats({ currentUserId: escrow.client.id, page: 1, limit: 50 })).unwrap();
			} catch (error) {
				// No existing chat found, will create new one
			}

			let chatResponse;
			if (existingChat.chats && existingChat.chats.length > 0) {
				const filteredChats = existingChat.chats.filter(chat => {
					const participantIds = chat.chat.participants.map(p => p.id);
					return participantIds.includes(escrow.client.id) && participantIds.includes(escrow.professional.id);
				});
				const chat = filteredChats[0].chat;
				chatResponse = chat;
			} else {
				// Create new chat
				const chatPayload = {
					unreadCount: { [escrow.client?.id]: 0, [escrow.professional?.id]: 1 },
					participants: [
						{ id: escrow.client?.id },
						{ id: escrow.professional?.id }
					],
					contextId: milestone?.contract?.id,
					contextType: { id: ContextEnum.Message },
					lastMessageAt: new Date().toISOString(),
					lastMessage: "Payment successful! Contract has been activated.",
				};

				chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
			}

			// Send payment success message
			const conversationPayload = {
				isRead: false,
				message: t("paymentModal.chat.paymentSuccessContract"),
				sender: { id: escrow.client?.id },
				chatId: { id: chatResponse.id },
				type: { id: ConversationTypeEnum.Contract },
				contextId: { id: escrow?.id },
				attachments: [],
			};

			const conversation = await dispatch(createConversation({ data: conversationPayload })).unwrap();
			setTimeout(async () => {
				// Send WebSocket message to notify participants
				try {
					const receivers = [escrow.professional.id];
					if (webSocketService.getConnectionStatus()) {
						const messageData = {
							receivers,
							sender: user?.id || "",
							type: "new_conversation",
							detail: {
								conversationId: conversation.id,
								conversation: conversation,
								chatId: conversation.chatId.id,
								message: conversationPayload.message,
								senderId: conversation.sender.id,
								attachments: conversationPayload.attachments,
								type: conversationPayload.type.id,
								referenceId: milestone?.contract?.id,
								timestamp: new Date().toISOString()
							},
							conversationId: conversationPayload.chatId.id
						};
						await webSocketService.sendMessage(messageData);
					}
				} catch (wsError) {
					console.warn("⚠️ Failed to send WebSocket message for payment success:", wsError);
				}
			}, 500);


		} catch (error) {
			console.error("Error creating chat or sending message:", error);
			// Don't throw error here as payment was successful
		}
	};

	// Function to send contract payment notification
	const sendContractPaymentNotification = async (escrow: Escrow) => {
		try {
			// Check if this is the first milestone
			const isFirstMilestone = milestone.id === selectedContract.milestones[0].id; // For fixed contracts, first milestone might equal contract amount

			if (!isFirstMilestone) {
				return;
			}

			// Prepare contract data for notification
			const contractData = {
				contractId: milestone?.contract?.id,
				amount: milestone?.amount || 0,
				senderId: milestone.contract.client.id,
				professional: milestone.contract.professional,
				currency: typeof escrow.currency === "string" ? escrow.currency : escrow.currency?.initial || "USD",
				professionalName: milestone?.contract?.professional?.firstName + " " + milestone?.contract?.professional?.lastName || "Professional",
				clientName: milestone?.contract?.client?.firstName + " " + milestone?.contract?.client?.lastName || "Client",
				chatId: escrow.id // Use escrow ID as chat reference
			};

			// Send the notification (role checking is done on the receiving side)
			await ContractNotificationService.sendContractPaymentNotification(contractData);

		} catch (error) {
			console.error("Error sending contract payment notification:", error);
			// Don't throw error as this is a notification, not critical to payment flow
		}
	};

	// Function to update job status and offer status to Contracted
	const updateJobStatusToContracted = async () => {
		try {
			// Get job ID and offer ID from milestone contract offer
			const jobId = milestone?.contract?.offer?.job?.id || milestone?.contract?.offer?.id;
			const offerId = milestone?.contract?.offer?.id;

			if (!jobId) {
				console.warn("No job ID found in milestone contract offer");
				return;
			}

			// Check if this is the first milestone
			const isFirstMilestone = milestone.id === selectedContract.milestones[0].id; // For fixed contracts, first milestone might equal contract amount

			if (!isFirstMilestone) {
				return;
			}

			// Update job status to Contracted (10)
			const jobUpdateData = {
				status: { id: JobStatusEnum.Contracted }
			};

			const jobResult = await dispatch(updateUserJob({ id: jobId, data: jobUpdateData }));

			if (updateUserJob.fulfilled.match(jobResult)) {
				// Job updated successfully
			} else {
				console.error("Failed to update job status:", jobResult.error);
			}

			// Update offer status to Contracted (15) if offer ID exists
			if (offerId) {
				const offerUpdateData = {
					status: { id: OfferStatusEnum.Contracted }
				};

				const offerResult = await dispatch(updateOffer({ id: offerId, data: offerUpdateData }));

				if (updateOffer.fulfilled.match(offerResult)) {
					GlobalService.showToast("success", t("common.success"), t("paymentModal.jobOfferStatusUpdated"));
				} else {
					console.error("Failed to update offer status:", offerResult.error);
					GlobalService.showToast("success", t("common.success"), t("paymentModal.jobStatusUpdated"));
				}
			} else {
				GlobalService.showToast("success", t("common.success"), t("paymentModal.jobStatusUpdated"));
			}
		} catch (error) {
			console.error("Error updating job/offer status:", error);
		}
	};

	useEffect(() => {
		if (isOpen) {
			setIsInitializing(true);
			setPaymentStep("form");
			setError(null);
			setEscrow(null);
			setRedirectCountdown(3);
			setUseExistingFunds(false);

			// Add a small delay to show the initialization loader
			const timer = setTimeout(() => {
				setIsInitializing(false);
			}, 500);

			return () => clearTimeout(timer);
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && !latestCategoryFormSetting) {
			dispatch(fetchLatestCategoryFormSettings());
		}
	}, [dispatch, isOpen, latestCategoryFormSetting]);

	// Fetch currencies, commissions, and quote pricings
	useEffect(() => {
		if (isOpen) {
			// Fetch currencies if not already loaded
			if (!currencies || currencies.length === 0) {
				dispatch(getAllCurrencies({ isShowLoader: false }));
			}
			// Fetch commissions if not already loaded
			if (!commissionRules || commissionRules.length === 0) {
				dispatch(fetchCommissions(undefined));
			}

			// Fetch quote pricings if not already loaded
			if ((!quotePricings || quotePricings.length === 0) && localQuotePricings.length === 0) {
				// Get category ID from milestone
				const categoryId = milestone?.contract?.offer?.job?.categoryId?.id;
				if (categoryId) {
					// Fetch quote pricings by category
					getQuotePricingsByCategory(categoryId).then((pricings) => {
						setLocalQuotePricings(pricings);
					}).catch(() => {
						// Silently handle error
					});
				}
			}
		}
	}, [isOpen, dispatch, currencies, commissionRules, quotePricings, milestone]);

	// Check available balance only once when modal opens
	useEffect(() => {
		if (isOpen) {
			checkAvailableBalance();
		}
	}, [isOpen, checkAvailableBalance]);

	// Sync selectedCurrency with selectedCurrencyId
	useEffect(() => {
		if (selectedCurrencyId && currencies && currencies.length > 0) {
			if (selectedCurrency?.id !== selectedCurrencyId) {
				const currency = currencies.find((c: any) => c.value === selectedCurrencyId);
				if (currency) {
					setSelectedCurrency({
						id: selectedCurrencyId,
						name: currency.label || "USD",
						code: currency.initial || "USD"
					});
				}
			}
		}
	}, [selectedCurrencyId, currencies, selectedCurrency?.id]);

	// Handle currency selection
	const handleCurrencySelect = (currency: {id: string; name: string; code: string} | null) => {
		if (currency) {
			setSelectedCurrency(currency);
			setSelectedCurrencyId(currency.id);
			// Update cache when user manually changes currency
			setCachedCurrencyPreference(currency.id);
		}
	};

	// Currency detection effect
	useEffect(() => {
		const detectAndSetCurrency = async () => {
			if (!currencies || currencies.length === 0 || !isOpen) {
				return;
			}

			setIsDetectingCurrency(true);
			try {
				// First check for cached preference
				const cachedCurrencyId = getCachedCurrencyPreference();
				if (cachedCurrencyId) {
					setSelectedCurrencyId(cachedCurrencyId);
					setIsDetectingCurrency(false);
					return;
				}

				// Detect currency based on user location
				const detectedCurrencyId = await useCurrencyDetection(
					currencies,
					(currencyId, source) => {
						setCachedCurrencyPreference(currencyId);
					},
					dispatch // Pass dispatch for backend API call
				);

				setSelectedCurrencyId(detectedCurrencyId);
			} catch (error) {
				console.error("Error detecting currency:", error);
				// Fallback to USD
				const usdCurrency = currencies.find((c: any) => c.initial === "USD");
				if (usdCurrency) {
					setSelectedCurrencyId(usdCurrency.value);
				}
			} finally {
				setIsDetectingCurrency(false);
			}
		};

		detectAndSetCurrency();
	}, [currencies, isOpen, dispatch]);

	// Countdown effect for redirect
	useEffect(() => {
		if (paymentStep === "success") {
			// Reset countdown to 3 when success step starts
			setRedirectCountdown(3);
			
			const timer = setInterval(() => {
				setRedirectCountdown((prev) => {
					if (prev <= 1) {
						clearInterval(timer);
						// Set loading state for closing
						setIsClosingAfterPayment(true);
						// Use setTimeout to avoid state update during render
						setTimeout(() => {
							// Check if user is already on myorders page
							if (location.pathname === "/client/myorders") {
								// Just close the dialog if already on myorders page
								onClose();
								onPaymentSuccess?.();
							} else {
								// Navigate to myorders if on different page
								localStorage.setItem("myOrdersSelectedTab", "active");
								navigate("/client/myorders");
								// Also close the modal and call success callback
								onClose();
								onPaymentSuccess?.();
							}
						}, 1000); // Small delay to show the loading state
						return 0;
					}
					return prev - 1;
				});
			}, 1000);

			return () => clearInterval(timer);
		}
	}, [paymentStep]);

	const handlePayment = async (paymentData: any) => {
		try {
			setPaymentStep("processing");
			setError(null);
			setLoading(true);
			const contract = milestone.contract;

			let escrowData: Escrow;

			// Check if escrow already exists for this milestone
			const existingEscrow = await escrowAPI.getEscrowByMilestone(milestone.id);

			if (existingEscrow) {
				escrowData = existingEscrow;

				// Calculate withdrawal amount for existing escrow if not already set
				if (!existingEscrow.withdrawlAmount) {
					const withdrawlAmount = await calculatewithdrawlAmount(milestone.amount, milestone.emergencyCost || 0);

					escrowData = await escrowAPI.updateEscrow(existingEscrow.id, {
						withdrawlAmount: withdrawlAmount
					});
				}

				// If escrow exists but is in failed status, update it to pending for retry
				const currentStatus = existingEscrow.status?.name?.toLowerCase() || existingEscrow.status?.toString().toLowerCase() || "";
				if (currentStatus === "failed" || currentStatus === "cancelled") {
					await escrowAPI.updateEscrow(existingEscrow.id, {
						status: "pending"
					});
					escrowData = { ...existingEscrow, status: { ...existingEscrow.status, name: "pending" } };
				}
			} else {
				// Create new escrow for milestone
				// Calculate withdrawal amount based on commission rules with tier support
				const withdrawlAmount = await calculatewithdrawlAmount(milestone.amount, milestone.emergencyCost);

				// Pass milestone amount and additional costs - the API will calculate platform fee internally
				escrowData = await escrowAPI.createMilestoneEscrow(
					contract.id,
					milestone.id,
					contract.client.id,
					contract.professional.id,
					milestone.amount, // Pass milestone amount (base amount)
					selectedCurrencyId,
					(milestone as any).emergencyCost || 0, // Emergency cost
					(milestone as any).trackerAmount || 0, // Smart Work Tracker amount
					paymentData.paymentMethodId, // Pass payment method ID for customer lookup
					categoryFormSetting
				);

				// Update the escrow with withdrawal amount after creation
				if (escrowData && escrowData.id) {
					escrowData = await escrowAPI.updateEscrow(escrowData.id, {
						withdrawlAmount: withdrawlAmount
					});
				}
			}

			setEscrow(escrowData);

			// Check if using existing funds
			if (paymentData.useExistingFunds) {
				// Using existing funds - no Stripe payment needed
				try {
					// Update escrow status to used_funds
					escrowData = await escrowAPI.updateEscrow(escrowData.id, {
						status: "funded"
					});

					escrowData = await escrowAPI.createMilestoneEscrow(
						contract.id,
						milestone.id,
						contract.client.id,
						"",
						milestone.amount, // Pass milestone amount (base amount)
						selectedCurrencyId,
						(milestone as any).emergencyCost || 0, // Emergency cost
						(milestone as any).trackerAmount || 0,
						categoryFormSetting
					);
					await escrowAPI.updateEscrow(escrowData.id, { status: "used_funds" });

					// create new escrow record with same amount and status used_funds
				} catch (escrowError: any) {
					console.error("Escrow update error:", escrowError);
					throw new Error(t("paymentModal.failedToProcessWallet"));
				}
			} else {
				// Process payment using backend API (using admin/add-funds pattern)
				try {
					// Get the selected currency info
					const selectedCurrency = currencies?.find((c: any) => c.value === selectedCurrencyId);
					const totalAmount = (Number(milestone.amount) || 0) + (Number((milestone as any).emergencyCost) || 0) + (Number((milestone as any).trackerAmount) || 0);
					const platformFeeAmount = categoryFormSetting
						? getPlatformFeeForContract(totalAmount, categoryFormSetting)
						: totalAmount * PLATFORM_CONFIG.PLATFORM_FEE_PERCENTAGE;

					// Call admin/add-funds API (same as AddFundsModal)
					const addFundsData = {
						amount: totalAmount + platformFeeAmount,
						currency: selectedCurrency?.code || "usd",
						paymentMethodId: paymentData.paymentMethodId,
						notes: `Milestone payment for ${milestone.description || "milestone"}`,
						escrowId: escrowData.id, // Include escrowId for webhook processing
					};

					const result = await escrowAPI.addFunds(addFundsData);

					// Update escrow status to funded (using the same pattern as AddFundsModal)
					escrowData = await escrowAPI.updateEscrow(escrowData.id, {
						status: "funded",
						stripePaymentIntentId: result.paymentIntentId,
						notes: `Milestone payment for ${milestone.description || "milestone"}` // Preserve notes
					});

				} catch (paymentError: any) {
					console.error("Payment processing error:", paymentError);

					// Update escrow status to failed
					try {
						await escrowAPI.updateEscrow(escrowData.id, {
							status: "failed"
						});
					} catch (updateError) {
						console.error("Failed to update escrow status:", updateError);
					}

					// Re-throw the error to be handled by the outer catch block
					throw paymentError;
				}
			}

			// Create milestone payment log with Funded status
			try {
				const paymentLogData: MilestonePaymentLogCreateDto = {
					milestone: { id: milestone.id },
					paymentStatus: { id: MilestonePaymentStatusEnum.Funded },
					reason: paymentData.useExistingFunds
						? "Payment successfully processed using wallet funds and held in escrow"
						: "Payment successfully processed and funds held in escrow"
				};

				await dispatch(createMilestonePaymentLog(paymentLogData)).unwrap();
			} catch (logError) {
				console.error("Error creating milestone payment log:", logError);
				// Don't throw here as payment was successful, just log the error
				GlobalService.showToast("warning", t("common.warning"), t("paymentModal.paymentSuccessLogWarning"));
			}

			// Update job status to Contracted
			await updateJobStatusToContracted();

			const isFirstMilestone = milestone.id === selectedContract.milestones[0].id;
			if (!isFirstMilestone) {
				// Update milestone status to Accepted for non-first milestones
				try {
					await dispatch(updateMilestone({
						id: milestone.id,
						data: {
							status: { id: MilestoneStatusEnum.Accepted },
						}
					})).unwrap();
				} catch (milestoneError) {
					console.error("Error updating milestone status:", milestoneError);
					// Don't throw here as payment was successful, just log the error
					GlobalService.showToast("warning", t("common.warning"), t("paymentModal.milestoneStatusWarning"));
				}
			}

			// Create chat and send payment success message
			await createChatAndSendMessage(escrowData);

			// Send contract payment notification for first milestone
			await sendContractPaymentNotification(escrowData);

			// Send commission tier discount notification for first milestone (if tier was applied)
			if (isFirstMilestone && (window as any).__tierDiscountInfo) {
				try {
					const tierInfo = (window as any).__tierDiscountInfo;
					await escrowAPI.sendCommissionDiscountNotification(
						tierInfo.professionalId,
						tierInfo.contractAmount,
						tierInfo.tierThreshold,
						tierInfo.discountPercentage,
						tierInfo.tierName
					);
					console.log("✅ Commission discount notification sent to professional");
					// Clear the stored info
					delete (window as any).__tierDiscountInfo;
				} catch (notifError) {
					console.error("❌ Failed to send commission discount notification:", notifError);
					// Don't break the flow if notification fails
				}
			}

			setPaymentStep("success");
			GlobalService.showToast("success", t("common.success"), paymentData.useExistingFunds
				? t("paymentModal.paymentSuccessWallet")
				: t("paymentModal.paymentSuccessStripe"));
		} catch (err: any) {
			setError(err.message || t("paymentModal.paymentFailedRetry"));
			setPaymentStep("error");
			GlobalService.showToast("error", t("common.error"), err.message || t("paymentModal.paymentFailed"));
		} finally {
			setLoading(false);
		}
	};

	const handleRetry = () => {
		setPaymentStep("form");
		setError(null);
	};

	if (!isOpen) return null;

	return (
		<AnimatePresence>
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				className="fixed inset-0 bg-black bg-opacity-50 z-[10003] flex items-center justify-center p-4"
				onClick={(e) => {
					if (e.target === e.currentTarget) onClose();
				}}
			>
				<motion.div
					initial={{ scale: 0.95, y: 20 }}
					animate={{ scale: 1, y: 0 }}
					exit={{ scale: 0.95, y: 20 }}
					className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative"
					onClick={(e) => e.stopPropagation()}
				>
					{/* Loading overlay when closing after payment */}
					{isClosingAfterPayment && (
						<div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-xl">
							<div className="flex flex-col items-center gap-3">
								<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
								<p className="text-sm text-gray-600 font-medium">{t("paymentModal.closingModal")}</p>
							</div>
						</div>
					)}
					<div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
						<div>
							<h2 className="text-xl font-bold text-gray-900">{t("paymentModal.title")}</h2>
							<p className="text-gray-600 mt-1">{t("paymentModal.subtitle")}</p>
						</div>
						<button
							onClick={onClose}
							className="p-2 hover:bg-gray-100 rounded-full transition-colors"
						>
							<X className="h-6 w-6 text-gray-500" />
						</button>
					</div>

					<div className="p-6">
						{paymentStep === "form" && (
							<div className="space-y-4">
								{/* Currency Selection */}
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										{t("paymentModal.selectCurrency")}
										{isDetectingCurrency && (
											<span className="ml-2 text-xs text-blue-600">
												{t("paymentModal.detectingLocation")}
											</span>
										)}
									</label>
									<CurrencyDropdown
										selectedCurrency={selectedCurrency}
										onCurrencySelect={handleCurrencySelect}
										placeholder={t("paymentModal.selectCurrencyPlaceholder")}
										disabled={isDetectingCurrency}
										autoDetect={true}
									/>
									{isDetectingCurrency && (
										<p className="mt-1 text-xs text-gray-500">
											{t("paymentModal.autoDetectingCurrency")}
										</p>
									)}
								</div>

								{/* Toggle between old and new form (for testing/compatibility) */}
								{useEnhancedForm ? (
									<EnhancedPaymentForm
										milestone={milestone}
										onPayment={handlePayment}
										loading={loading}
										availableBalance={availableBalance}
										checkingBalance={checkingBalance}
										useExistingFunds={useExistingFunds}
										setUseExistingFunds={setUseExistingFunds}
										categoryFormSetting={categoryFormSetting}
									/>
								) : (
									<PaymentForm
										milestone={milestone}
										onPayment={handlePayment}
										loading={loading}
										categoryFormSetting={categoryFormSetting}
									/>
								)}
							</div>
						)}

						{paymentStep === "processing" && (
							<div className="text-center py-12">
								<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
								<h3 className="text-lg font-semibold text-gray-900 mb-2">{t("paymentModal.processingPayment")}</h3>
								<p className="text-gray-600">{t("paymentModal.processingMessage")}</p>
							</div>
						)}

						{paymentStep === "success" && (
							<div className="text-center py-12">
								<CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
								<h3 className="text-lg font-semibold text-gray-900 mb-2">{t("paymentModal.paymentSuccessful")}</h3>
								<p className="text-gray-600 mb-4">
									{t("paymentModal.paymentSuccessMessage")}
								</p>
								<p className="text-sm text-blue-600 mb-6">
									{t("paymentModal.redirectingMessage", { countdown: redirectCountdown })}
								</p>

								{escrow && (
									<Card className="p-4 mb-6 text-left">
										<h4 className="font-semibold text-gray-900 mb-3">{t("paymentModal.paymentDetails")}</h4>
										<div className="space-y-2 text-sm">
											<div className="flex justify-between">
												<span className="text-gray-600">{t("paymentModal.escrowId")}</span>
												<span className="font-mono">{escrow.id}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-gray-600">{t("paymentModal.amount")}</span>
												<span>{getDynamicCurrencySymbol()}{escrow.amount}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-gray-600">{t("paymentModal.status")}</span>
												<span className="text-green-600 font-medium">{typeof escrow.status === "string" ? escrow.status : escrow.status?.name || t("paymentModal.unknown")}</span>
											</div>
										</div>
									</Card>
								)}

								<div className="flex gap-3 justify-center">
									<Button
										onClick={() => {
											setIsClosingAfterPayment(true);
											setTimeout(() => {
												onClose();
												onPaymentSuccess?.();
											}, 1000);
										}}
										className="bg-blue-600 hover:bg-blue-700"
										disabled={isClosingAfterPayment}
									>
										{isClosingAfterPayment ? t("paymentModal.closing") : t("paymentModal.close")}
									</Button>
									<Button
										onClick={() => {
											setIsClosingAfterPayment(true);
											// Use setTimeout to avoid state update during render
											setTimeout(() => {
												onClose();
												onPaymentSuccess?.();
												// Check if user is already on myorders page
												if (location.pathname === "/client/myorders") {
													// Just close the dialog if already on myorders page
													return;
												} else {
													// Navigate to myorders if on different page
													localStorage.setItem("myOrdersSelectedTab", "active");
													navigate("/client/myorders");
												}
											}, 1000);
										}}
										variant="outline"
										disabled={isClosingAfterPayment}
									>
										{isClosingAfterPayment ? t("paymentModal.redirecting") : t("paymentModal.goToMyOrders")}
									</Button>
								</div>
							</div>
						)}

						{paymentStep === "error" && (
							<div className="text-center py-12">
								<AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
								<h3 className="text-lg font-semibold text-gray-900 mb-2">{t("paymentModal.paymentFailed")}</h3>
								<p className="text-gray-600 mb-6">{error}</p>

								<div className="flex gap-3 justify-center">
									<Button onClick={handleRetry} className="bg-blue-600 hover:bg-blue-700">
										{t("paymentModal.tryAgain")}
									</Button>
									<Button onClick={onClose} variant="outline">
										{t("paymentModal.cancel")}
									</Button>
								</div>
							</div>
						)}
					</div>
				</motion.div>
			</motion.div>
		</AnimatePresence>
	);
};

export default PaymentModal;
